import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

type ScrollMode = 'inner' | 'page' | 'none';
type PadMode = 'bottom-nav' | 'none';

interface PageShellProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * 'inner'  → fixed-height shell, an inner scroll container handles overflow (the standard app pattern).
   * 'page'   → naturally scrolling page (marketing pages like /terms, /safety).
   * 'none'   → no scroll, content owns layout (full-screen canvas pages).
   * @default 'inner'
   */
  scroll?: ScrollMode;
  /**
   * 'bottom-nav' → adds bottom padding so content clears the mobile BottomNav. Inner scroll only.
   * @default 'none'
   */
  pad?: PadMode;
  /** Slot rendered above the scroll region (sticky header, toolbar, etc.). Not scrolled. */
  topSlot?: ReactNode;
  /** Optional inner scroll container className (only used when scroll === 'inner'). */
  scrollClassName?: string;
}

/**
 * Canonical CardSimp page wrapper.
 *
 * Why this exists:
 * - iOS Safari's URL bar collapse/expand resizes `100vh`. Pages that scroll the body
 *   visibly shift. The fix is: lock the outer shell to `100dvh` and scroll an inner
 *   container instead. This component enforces that.
 * - Single source of truth for safe-area handling + bottom-nav padding.
 *
 * Replaces the ad-hoc:
 *   <div className="h-screen-safe flex flex-col bg-background overflow-hidden">...</div>
 */
export const PageShell = forwardRef<HTMLDivElement, PageShellProps>(
  (
    {
      scroll = 'inner',
      pad = 'none',
      topSlot,
      scrollClassName,
      className,
      children,
      ...rest
    },
    ref
  ) => {
    if (scroll === 'page') {
      return (
        <div
          ref={ref}
          className={cn(
            'min-h-screen-safe flex flex-col bg-background',
            className
          )}
          {...rest}
        >
          {topSlot}
          {children}
        </div>
      );
    }

    if (scroll === 'none') {
      return (
        <div
          ref={ref}
          className={cn(
            'h-screen-safe flex flex-col bg-background overflow-hidden',
            className
          )}
          {...rest}
        >
          {topSlot}
          {children}
        </div>
      );
    }

    // scroll === 'inner' (default)
    return (
      <div
        ref={ref}
        className={cn(
          'h-screen-safe flex flex-col bg-background overflow-hidden',
          className
        )}
        {...rest}
      >
        {topSlot}
        <div
          className={cn(
            'flex-1 min-h-0 overflow-y-auto overflow-x-hidden main-content-scroll',
            pad === 'bottom-nav' && 'pb-bottom-nav',
            scrollClassName
          )}
        >
          {children}
        </div>
      </div>
    );
  }
);
PageShell.displayName = 'PageShell';
